import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { stlAPI } from '../lib/api';
import {
  Upload,
  FileText,
  AlertTriangle,
  CheckCircle,
  Loader,
  X,
  Download,
} from 'lucide-react';

const STLAnalysis = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      if (selectedFile.name.toLowerCase().endsWith('.stl')) {
        setFile(selectedFile);
        setError('');
      } else {
        setError('Please upload a valid STL file');
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/sla': ['.stl'] },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const handleUploadAndAnalyze = async () => {
    if (!file) return;

    setUploading(true);
    setError('');

    try {
      // Upload file
      const uploadRes = await stlAPI.upload(file);
      const { analysis_id } = uploadRes.data;

      setUploading(false);
      setAnalyzing(true);

      // Poll for analysis results
      let attempts = 0;
      const maxAttempts = 30;
      const pollInterval = 2000;

      const poll = async () => {
        try {
          const analysisRes = await stlAPI.analyze(analysis_id);
          const data = analysisRes.data;

          if (data.status === 'completed') {
            setAnalysis(data);
            setAnalyzing(false);
          } else if (data.status === 'failed') {
            setError('Analysis failed: ' + (data.error_message || 'Unknown error'));
            setAnalyzing(false);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, pollInterval);
          } else {
            setError('Analysis timed out. Please try again.');
            setAnalyzing(false);
          }
        } catch (err) {
          setError('Failed to get analysis results');
          setAnalyzing(false);
        }
      };

      poll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
      setUploading(false);
      setAnalyzing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setAnalysis(null);
    setError('');
  };

  return (
    <div className="max-w-6xl" data-testid="stl-analysis-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">STL File Analysis</h1>
        <p className="text-text-secondary">
          Upload your 3D model for AI-powered printability analysis and optimization recommendations
        </p>
      </div>

      {!analysis ? (
        <div className="space-y-6">
          {/* Upload Area */}
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition
              ${isDragActive
                ? 'border-primary bg-primary/10'
                : file
                ? 'border-accent bg-accent/10'
                : 'border-text-muted/20 bg-bg-light hover:border-primary/50'
              }
            `}
            data-testid="stl-dropzone"
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center">
              {file ? (
                <>
                  <FileText className="w-16 h-16 text-accent mb-4" />
                  <p className="text-xl font-semibold text-white mb-2">{file.name}</p>
                  <p className="text-text-secondary mb-4">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    className="text-error hover:text-error/80 text-sm flex items-center space-x-2"
                  >
                    <X className="w-4 h-4" />
                    <span>Remove file</span>
                  </button>
                </>
              ) : (
                <>
                  <Upload className="w-16 h-16 text-text-muted mb-4" />
                  <p className="text-xl font-semibold text-white mb-2">
                    {isDragActive ? 'Drop your STL file here' : 'Drag & drop your STL file'}
                  </p>
                  <p className="text-text-secondary mb-4">or click to browse</p>
                  <p className="text-sm text-text-muted">Maximum file size: 50MB</p>
                </>
              )}
            </div>
          </div>

          {error && (
            <div className="p-4 bg-error/10 border border-error/20 rounded-lg flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          {file && (
            <button
              onClick={handleUploadAndAnalyze}
              disabled={uploading || analyzing}
              className="w-full py-4 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              data-testid="analyze-stl-btn"
            >
              {uploading || analyzing ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>{uploading ? 'Uploading...' : 'Analyzing...'}</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  <span>Analyze STL File</span>
                </>
              )}
            </button>
          )}

          {/* Info Cards */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-bg-light p-4 rounded-lg border border-text-muted/20">
              <h3 className="font-semibold text-white mb-2">Geometry Analysis</h3>
              <p className="text-sm text-text-secondary">
                Surface count, volume, wall thickness detection
              </p>
            </div>
            <div className="bg-bg-light p-4 rounded-lg border border-text-muted/20">
              <h3 className="font-semibold text-white mb-2">Printability Score</h3>
              <p className="text-sm text-text-secondary">
                AI-powered assessment of print success likelihood
              </p>
            </div>
            <div className="bg-bg-light p-4 rounded-lg border border-text-muted/20">
              <h3 className="font-semibold text-white mb-2">Recommendations</h3>
              <p className="text-sm text-text-secondary">
                Orientation, support, and modification suggestions
              </p>
            </div>
          </div>
        </div>
      ) : (
        /* Analysis Results */
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-8 h-8 text-success" />
              <div>
                <h2 className="text-2xl font-bold text-white">Analysis Complete</h2>
                <p className="text-text-secondary">{file?.name}</p>
              </div>
            </div>
            <button
              onClick={handleReset}
              className="px-6 py-2 bg-bg-light hover:bg-[#252936] text-white rounded-lg transition"
            >
              Analyze Another
            </button>
          </div>

          {/* Printability Score */}
          <div className="bg-gradient-to-br from-primary/20 to-accent/20 p-6 rounded-xl border border-primary/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Printability Score</h3>
              <div className="text-4xl font-bold text-white">
                {analysis.ai_report?.printability_score || 85}/100
              </div>
            </div>
            <div className="w-full bg-bg-dark rounded-full h-3">
              <div
                className="bg-gradient-to-r from-primary to-accent h-3 rounded-full transition-all"
                style={{ width: `${analysis.ai_report?.printability_score || 85}%` }}
              />
            </div>
          </div>

          {/* Issues */}
          {analysis.ai_report?.issues && analysis.ai_report.issues.length > 0 && (
            <div className="bg-bg-light p-6 rounded-xl border border-error/20">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-error" />
                <span>Detected Issues</span>
              </h3>
              <ul className="space-y-2">
                {analysis.ai_report.issues.map((issue, index) => (
                  <li key={index} className="flex items-start space-x-2 text-text-secondary">
                    <span className="text-error mt-1">•</span>
                    <span>{issue}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Fixes */}
          {analysis.ai_report?.fixes && analysis.ai_report.fixes.length > 0 && (
            <div className="bg-bg-light p-6 rounded-xl border border-accent/20">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-accent" />
                <span>Recommended Fixes</span>
              </h3>
              <ul className="space-y-2">
                {analysis.ai_report.fixes.map((fix, index) => (
                  <li key={index} className="flex items-start space-x-2 text-text-secondary">
                    <span className="text-accent mt-1">✓</span>
                    <span>{fix}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Orientation */}
          {analysis.ai_report?.recommended_orientation && (
            <div className="bg-bg-light p-6 rounded-xl border border-primary/20">
              <h3 className="text-lg font-bold text-white mb-3">Recommended Orientation</h3>
              <p className="text-text-secondary leading-relaxed">
                {analysis.ai_report.recommended_orientation}
              </p>
            </div>
          )}

          {/* Geometry Data */}
          {analysis.geometry_data && (
            <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20">
              <h3 className="text-lg font-bold text-white mb-4">Geometry Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-text-muted mb-1">Faces</p>
                  <p className="text-lg font-semibold text-white">
                    {analysis.geometry_data.num_faces?.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-text-muted mb-1">Vertices</p>
                  <p className="text-lg font-semibold text-white">
                    {analysis.geometry_data.num_vertices?.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-text-muted mb-1">Volume</p>
                  <p className="text-lg font-semibold text-white">
                    {analysis.geometry_data.volume?.toFixed(2)} mm³
                  </p>
                </div>
                <div>
                  <p className="text-sm text-text-muted mb-1">Surface Area</p>
                  <p className="text-lg font-semibold text-white">
                    {analysis.geometry_data.surface_area?.toFixed(2)} mm²
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default STLAnalysis;
